import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getEmailSettings, saveEmailSettings, resetEmailSettings, testEmail, renderTemplate, TEMPLATE_DEFS, DEFAULT_EMAIL_SETTINGS, type EmailSettings } from "../lib/email";
import { clearEmailLog, adminGetHandbook, adminUploadHandbook, adminDeleteHandbook, type HandbookFile } from "../../lib/api";
import { logAudit } from "../lib/audit";

type TestState = "idle" | "sending" | "ok" | "error";
const HANDBOOK_MAX_BYTES = 20 * 1024 * 1024;

function formatBytes(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** Email / SMTP tab (ot) — send rules, SMTP server, test, contacts, and templates. */
export function SmtpTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [s, setS] = useState<EmailSettings>(DEFAULT_EMAIL_SETTINGS);
  const [dirty, setDirty] = useState(false);
  const [tpl, setTpl] = useState<string>("completion");
  const [tplLang, setTplLang] = useState<Lang>(lang);
  const [testTo, setTestTo] = useState("");
  const [testState, setTestState] = useState<TestState>("idle");
  const [testMsg, setTestMsg] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  const [handbook, setHandbook] = useState<HandbookFile | null>(null);
  const [handbookLoading, setHandbookLoading] = useState(true);
  const [handbookBusy, setHandbookBusy] = useState(false);

  useEffect(() => {
    adminGetHandbook().then((res) => { setHandbook(res.ok ? res.file : null); setHandbookLoading(false); });
  }, []);

  function uploadHandbook(file: File | undefined) {
    if (!file) return;
    if (file.size > HANDBOOK_MAX_BYTES) { toast.error(t(`حجم الملف كبير جداً (الحد ${HANDBOOK_MAX_BYTES / 1024 / 1024} ميجابايت)`, `File too large (max ${HANDBOOK_MAX_BYTES / 1024 / 1024} MB)`)); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const contentBase64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
      setHandbookBusy(true);
      const res = await adminUploadHandbook(file.name, file.type || "application/octet-stream", contentBase64);
      setHandbookBusy(false);
      if (res.ok && res.file) {
        setHandbook(res.file);
        toast.success(t("تم رفع دليل الموظف", "Employee handbook uploaded"));
        logAudit("handbook_uploaded", `Handbook file uploaded: ${file.name}`);
      } else {
        toast.error(t("تعذّر الرفع: ", "Upload failed: ") + (res.error || ""));
      }
    };
    reader.onerror = () => toast.error(t("تعذّر قراءة الملف", "Could not read the file"));
    reader.readAsDataURL(file);
  }

  async function removeHandbook() {
    if (!confirm(t("إزالة دليل الموظف؟ لن يُرفق بعد الآن مع رسائل إتمام الرحلة.", "Remove the employee handbook? It will no longer be attached to completion emails."))) return;
    setHandbookBusy(true);
    const res = await adminDeleteHandbook();
    setHandbookBusy(false);
    if (res.ok) {
      setHandbook(null);
      toast.success(t("تمت الإزالة", "Removed"));
      logAudit("handbook_removed", "Handbook file removed");
    } else {
      toast.error(t("تعذّرت الإزالة: ", "Remove failed: ") + (res.error || ""));
    }
  }

  async function resetEmailHistory() {
    const email = resetEmail.trim();
    if (!email) return;
    setResetting(true);
    const res = await clearEmailLog(email);
    setResetting(false);
    if (res.ok && res.data?.ok) {
      toast.success(t(`تم مسح سجل الإيميل (${res.data.cleared ?? 0} سجل)، يمكن الآن إعادة إرسال رسائل الترحيب والإتمام لهذا البريد.`, `Email history cleared (${res.data.cleared ?? 0} rows), welcome/completion emails can be re-sent to this address now.`));
      setResetEmail("");
    } else {
      toast.error(t("تعذّر المسح: ", "Clear failed: ") + (res.error || res.data?.error || ""));
    }
  }

  useEffect(() => { getEmailSettings().then((v) => { setS(v); setDirty(false); }); }, []);
  const set = (key: keyof EmailSettings, val: any) => { setS((prev) => ({ ...prev, [key]: val })); setDirty(true); };

  async function save() {
    const res = await saveEmailSettings(s);
    res.ok ? (setDirty(false), toast.success(t("تم حفظ إعدادات البريد", "Email settings saved")), logAudit("smtp_updated", "SMTP/email settings saved")) : toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
  }
  async function reset() {
    if (confirm(t("استعادة الإعدادات الافتراضية لـ SMTP والقوالب؟", "Reset SMTP settings and templates to defaults?"))) {
      const res = await resetEmailSettings();
      if (res.ok) { setS(await getEmailSettings()); setDirty(false); toast.success(t("تمت الاستعادة", "Reset")); }
      else toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
    }
  }
  async function runTest() {
    if (!testTo.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) { setTestState("error"); setTestMsg(t("يرجى إدخال بريد صحيح للاختبار", "Please enter a valid email to test")); return; }
    setTestState("sending"); setTestMsg("");
    try {
      const res = await testEmail(testTo);
      if (res.ok) { setTestState("ok"); setTestMsg(t(`تم الإرسال بنجاح إلى ${testTo}. تحقّق من البريد الوارد (وصندوق الرسائل غير المرغوب فيها).`, `Test email sent to ${testTo}. Check the inbox (and spam folder).`)); logAudit("smtp_test_ok", `SMTP test sent to ${testTo}`); }
      else { setTestState("error"); setTestMsg(t(`فشل الاختبار: ${res.error || "unknown error"}`, `Test failed: ${res.error || "unknown error"}`)); logAudit("smtp_test_fail", `SMTP test to ${testTo}: ${res.error}`); }
    } catch (err) {
      setTestState("error");
      setTestMsg(t(`خطأ غير متوقع: ${err}`, `Unexpected error: ${err}`));
    }
  }

  const def = TEMPLATE_DEFS[tpl];
  const field = tplLang === "ar" ? def.arField : def.enField;
  const templateHtml = (s[field] as string) || "";
  function resetTemplate() {
    if (confirm(t("استعادة القالب الافتراضي؟", "Restore default template?"))) set(field, "");
  }
  const previewVars: Record<string, string> = {
    NAME: t("محمد أحمد", "Mohamed Ahmed"), ROLE: t("محاسب", "Accountant"), PATH: t("المالية", "Finance"),
    DATE: new Date().toLocaleDateString(tplLang === "ar" ? "ar-SA" : "en-US"), DURATION: tplLang === "ar" ? "12 د" : "12m",
    DAYS_AGO: "3", LAST_STEP: tplLang === "ar" ? "الأنظمة" : "Systems", IT_EMAIL: s.itContactEmail, IT_EXT: s.itContactExt,
    HR_EMAIL: s.hrContactEmail, HR_EXT: s.hrContactExt, PORTAL_URL: typeof window !== "undefined" ? window.location.origin + "/" : "", YEAR: new Date().getFullYear().toString(),
  };
  const preview = useMemo(() => renderTemplate(templateHtml, previewVars), [templateHtml, s]);

  return (
    <div className="kd-admin-smtp">
      <div className="kd-admin-info-banner">
        <strong>{t("ملاحظة:", "Note:")}</strong>{" "}
        {t("الإرسال الفعلي للبريد يحتاج إلى خادم خلفي (مثل Cloudflare Worker) بإعدادات SMTP صحيحة.", "Actual email sending requires a backend (e.g. a Cloudflare Worker) with valid SMTP settings.")}
      </div>
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary" onClick={save} disabled={!dirty}>{dirty ? t("حفظ التعديلات", "Save Changes") : t("محفوظ", "Saved")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset to defaults")}</button>
      </div>

      <fieldset className="kd-admin-fieldset kd-admin-rules">
        <legend>{t("الحالة العامة وقواعد الإرسال", "General Status & Send Rules")}</legend>
        <div className="kd-admin-rule-row kd-admin-rule-master">
          <label className="kd-admin-checkbox kd-admin-rule-toggle">
            <input type="checkbox" checked={s.enabled} onChange={(e) => set("enabled", e.target.checked)} />
            <span className="kd-admin-rule-label">{t("تفعيل خدمة البريد", "Enable email service")}</span>
          </label>
          <span className={`kd-admin-pill ${s.enabled ? "on" : "off"}`}>{s.enabled ? t("مفعّل", "ENABLED") : t("معطّل", "DISABLED")}</span>
        </div>
        <div className="kd-admin-rule-row">
          <label className="kd-admin-checkbox kd-admin-rule-toggle"><input type="checkbox" checked={s.sendWelcome} onChange={(e) => set("sendWelcome", e.target.checked)} disabled={!s.enabled} /><span className="kd-admin-rule-label">{t("إرسال بريد ترحيب عند بدء الرحلة", "Send welcome email at journey start")}</span></label>
        </div>
        <div className="kd-admin-rule-row" style={{ gap: "1.5rem", flexWrap: "wrap" }}>
          <label className="kd-admin-checkbox kd-admin-rule-toggle"><input type="checkbox" checked={s.sendManagerNotice} onChange={(e) => set("sendManagerNotice", e.target.checked)} disabled={!s.enabled} /><span className="kd-admin-rule-label">{t("إشعار المدير المباشر عند الإكمال", "Notify direct manager on completion")}</span></label>
          <label className="kd-admin-checkbox kd-admin-rule-toggle"><input type="checkbox" checked={s.ccHrOnManagerNotice} onChange={(e) => set("ccHrOnManagerNotice", e.target.checked)} disabled={!s.enabled} /><span className="kd-admin-rule-label">{t("إشعار الموارد البشرية عند الإكمال", "Notify HR on completion")}</span></label>
          <label className="kd-admin-checkbox kd-admin-rule-toggle"><input type="checkbox" checked={s.ccItOnManagerNotice} onChange={(e) => set("ccItOnManagerNotice", e.target.checked)} disabled={!s.enabled} /><span className="kd-admin-rule-label">{t("إشعار تقنية المعلومات عند الإكمال", "Notify IT on completion")}</span></label>
        </div>
        <p className="kd-admin-hint">
          {t(
            "هذا هو نفسه الإشعار الذي يحمل قائمة البنود المعلّقة في قائمة المراجعة الأساسية إن وُجدت. الخيارات الثلاثة مستقلة تمامًا عن بعضها، فعّل أيًا منها بمفرده أو بأي توليفة تناسبك، وسيصل الإشعار فقط للجهات المفعّلة (لو مفعّل واحد فقط، يصله كمستلم أساسي مباشرة).",
            "This is the same notice that carries any pending checklist items. The three options are fully independent, enable any one alone or any combination, and the notice only goes to whichever are enabled (if only one is on, it's sent directly to that recipient).",
          )}
        </p>
        <div className="kd-admin-rule-row kd-admin-rule-with-input">
          <label className="kd-admin-checkbox kd-admin-rule-toggle"><input type="checkbox" checked={s.sendReminders} onChange={(e) => set("sendReminders", e.target.checked)} disabled={!s.enabled} /><span className="kd-admin-rule-label">{t("إرسال تذكير للموظفين الذين لم يكملوا الرحلة", "Send reminders to employees who did not finish")}</span></label>
          <label className="kd-admin-rule-inline-input"><span>{t("بعد كم يوم؟", "After how many days?")}</span><input type="number" min={1} max={30} value={s.reminderDays} onChange={(e) => set("reminderDays", Math.max(1, Number(e.target.value) || 1))} disabled={!s.enabled || !s.sendReminders} /></label>
        </div>
      </fieldset>

      <fieldset className="kd-admin-fieldset">
        <legend>{t("بيانات خادم SMTP", "SMTP Server Settings")}</legend>
        <div className="kd-admin-grid-2">
          <label><span>{t("المضيف (Host)", "Host")}</span><input value={s.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.office365.com" /></label>
          <label><span>{t("المنفذ (Port)", "Port")}</span><input type="number" value={s.port} onChange={(e) => set("port", Number(e.target.value))} /></label>
        </div>
        <label className="kd-admin-checkbox"><input type="checkbox" checked={s.secure} onChange={(e) => set("secure", e.target.checked)} /><span>{t("استخدام SSL/TLS مباشرة (للمنفذ 465). للمنفذ 587 اتركها معطّلة (STARTTLS)", "Use direct SSL/TLS (for port 465). For port 587 leave off (STARTTLS).")}</span></label>
        <div className="kd-admin-grid-2">
          <label><span>{t("اسم المستخدم", "Username")}</span><input value={s.username} onChange={(e) => set("username", e.target.value)} autoComplete="off" /></label>
          <label><span>{t("كلمة المرور / مفتاح التطبيق", "Password / App Key")}</span><input type="password" value={s.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" /></label>
        </div>
        <div className="kd-admin-grid-2">
          <label><span>{t("المُرسل (From Email)", "From Email")}</span><input value={s.fromEmail} onChange={(e) => set("fromEmail", e.target.value)} /></label>
          <label><span>{t("اسم المُرسل", "From Name")}</span><input value={s.fromName} onChange={(e) => set("fromName", e.target.value)} /></label>
        </div>
      </fieldset>

      <fieldset className="kd-admin-fieldset kd-admin-test">
        <legend>{t("اختبار الاتصال بخادم البريد", "Test SMTP Connection")}</legend>
        <p className="kd-admin-hint">{t("سيتم إرسال رسالة اختبارية إلى البريد المُدخل أدناه باستخدام الإعدادات المحفوظة على السيرفر.", "A test email will be sent to the address below using the server-saved settings.")}</p>
        <div className="kd-admin-test-row">
          <input type="email" placeholder={t("البريد المستلم للاختبار", "Test recipient email")} value={testTo} onChange={(e) => setTestTo(e.target.value)} className="kd-admin-test-email" />
          <button type="button" className="kd-btn-primary kd-btn-gold" onClick={runTest} disabled={testState === "sending" || dirty} title={dirty ? t("احفظ التغييرات قبل الاختبار", "Save changes before testing") : ""}>
            {testState === "sending" ? <span className="kd-spinner" /> : t("إرسال رسالة اختبارية", "Send test email")}
          </button>
        </div>
        {dirty && <div className="kd-admin-test-result" style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>ⓘ {t("الاختبار بيستخدم الإعدادات المحفوظة على السيرفر، احفظ التغييرات أولاً.", "Test uses the server-saved settings, save your changes first.")}</div>}
        {testState === "ok" && <div className="kd-admin-test-result kd-admin-test-ok">✓ {testMsg}</div>}
        {testState === "error" && <div className="kd-admin-test-result kd-admin-test-err">✕ {testMsg}</div>}

        <div className="kd-admin-test-row" style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px dashed var(--kd-card-border)" }}>
          <input type="email" placeholder="name@kaden-sa.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="kd-admin-test-email" />
          <button type="button" className="kd-btn-secondary" onClick={resetEmailHistory} disabled={resetting || !resetEmail.trim()}>
            {resetting ? <span className="kd-spinner" /> : t("إعادة تعيين سجل الإيميل لموظف", "Reset an employee's email history")}
          </button>
        </div>
        <p className="kd-admin-hint">
          {t(
            "رسائل الترحيب والإتمام تُرسَل مرة واحدة فقط لكل موظف بشكل دائم، استخدم هذا لإعادة اختبار الرحلة بنفس البريد.",
            "Welcome and completion emails are permanently sent only once per employee, use this to re-test the journey with the same address.",
          )}
        </p>
      </fieldset>

      <fieldset className="kd-admin-fieldset">
        <legend>{t("بيانات التواصل في الرسالة", "Message Contact Details")}</legend>
        <div className="kd-admin-grid-2">
          <label><span>{t("بريد تقنية المعلومات", "IT Email")}</span><input value={s.itContactEmail} onChange={(e) => set("itContactEmail", e.target.value)} /></label>
          <label><span>{t("تحويلة تقنية المعلومات", "IT Ext.")}</span><input value={s.itContactExt} onChange={(e) => set("itContactExt", e.target.value)} /></label>
        </div>
        <div className="kd-admin-grid-2">
          <label><span>{t("بريد الموارد البشرية", "HR Email")}</span><input value={s.hrContactEmail} onChange={(e) => set("hrContactEmail", e.target.value)} /></label>
          <label><span>{t("تحويلة الموارد البشرية", "HR Ext.")}</span><input value={s.hrContactExt} onChange={(e) => set("hrContactExt", e.target.value)} /></label>
        </div>
      </fieldset>

      <fieldset className="kd-admin-fieldset">
        <legend>{t("دليل الموظف", "Employee Handbook")}</legend>
        <p className="kd-admin-hint">
          {t(
            "يُرفق هذا الملف تلقائياً مع رسالة إتمام رحلة التأهيل لكل موظف، ليبقى معه للمرجعية، ارفع نسخة محدّثة هنا في أي وقت لتحل محل الملف الحالي.",
            "This file is attached automatically to every employee's onboarding-completion email, for their future reference. Upload an updated copy here anytime to replace the current one.",
          )}
        </p>
        {handbookLoading ? (
          <div className="kd-admin-hint">{t("جارٍ التحميل...", "Loading...")}</div>
        ) : handbook ? (
          <div className="kd-admin-test-row" style={{ alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
              <strong>{handbook.filename}</strong>
              <span className="kd-admin-hint" style={{ margin: 0 }}>
                {formatBytes(handbook.size)} · {new Date(handbook.uploadedAt).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")}
              </span>
            </div>
            <label className="kd-admin-upload-btn">
              ⬆ {t("استبدال الملف", "Replace file")}
              <input type="file" style={{ display: "none" }} disabled={handbookBusy} onChange={(e) => { uploadHandbook(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <button type="button" className="kd-btn-secondary kd-btn-danger" onClick={removeHandbook} disabled={handbookBusy}>
              {t("إزالة", "Remove")}
            </button>
          </div>
        ) : (
          <div className="kd-admin-test-row" style={{ alignItems: "center" }}>
            <span className="kd-admin-hint" style={{ margin: 0 }}>{t("لا يوجد ملف مرفوع حالياً.", "No file uploaded yet.")}</span>
            <label className="kd-admin-upload-btn">
              ⬆ {t("رفع دليل الموظف", "Upload handbook")}
              <input type="file" style={{ display: "none" }} disabled={handbookBusy} onChange={(e) => { uploadHandbook(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        )}
      </fieldset>

      <fieldset className="kd-admin-fieldset">
        <legend>{t("قوالب الرسائل (HTML)", "Email Templates (HTML)")}</legend>
        <div className="kd-admin-tpl-tabs kd-admin-tpl-tabs-row">
          {Object.keys(TEMPLATE_DEFS).map((key) => (
            <button key={key} className={`kd-admin-tpl-tab${tpl === key ? " active" : ""}`} onClick={() => setTpl(key)}>{lang === "ar" ? TEMPLATE_DEFS[key].ar : TEMPLATE_DEFS[key].en}</button>
          ))}
        </div>
        <p className="kd-admin-hint">
          {t("المتغيرات المتاحة لهذا القالب:", "Variables for this template:")} {def.vars.map((v) => <code className="kd-admin-var" key={v}>{`{{${v}}}`}</code>)}
        </p>
        <div className="kd-admin-tpl-tabs">
          <button className={`kd-admin-tpl-tab${tplLang === "ar" ? " active" : ""}`} onClick={() => setTplLang("ar")}>عربي</button>
          <button className={`kd-admin-tpl-tab${tplLang === "en" ? " active" : ""}`} onClick={() => setTplLang("en")}>English</button>
          <button className="kd-btn-secondary kd-btn-sm" onClick={resetTemplate} style={{ marginInlineStart: "auto" }}>⟲ {t("استعادة هذا القالب", "Reset this template")}</button>
        </div>
        <div className="kd-admin-tpl-grid">
          <textarea rows={18} className="kd-admin-tpl-editor" value={templateHtml} onChange={(e) => set(field, e.target.value)} spellCheck={false} placeholder={t("(فارغ = استخدام القالب الافتراضي من السيرفر)", "(empty = use the server default template)")} />
          <div className="kd-admin-tpl-preview">
            <div className="kd-admin-tpl-preview-label">{t("معاينة مباشرة", "Live preview")}</div>
            <iframe srcDoc={preview} title="Preview" />
          </div>
        </div>
      </fieldset>
    </div>
  );
}
